/**
 * AI Model Pricing Configuration
 *
 * Contains pricing rates for all AI models used in the application.
 * Rates are in USD per 1 million tokens unless otherwise noted.
 *
 * Last updated: January 2026
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import { formatUsd } from '../components/common/CopyableAmount/utils';
import { USER_COST_MULTIPLIER } from './agenticWorkflowConfig';
import { getPublicSystemNameForUsage } from './providerCodeNames';

// ============================================================================
// Pricing Types
// ============================================================================

export interface ModelPricing {
  /** Price per 1M input tokens in USD */
  inputPerMillion: number;
  /** Price per 1M output tokens in USD */
  outputPerMillion: number;
  /** Price per 1M reasoning tokens in USD (optional) */
  reasoningPerMillion?: number;
  /** Price per 1M citation tokens in USD (optional, Perplexity) */
  citationPerMillion?: number;
  /** Price per 1K search queries in USD (optional, Perplexity) */
  searchPerThousand?: number;
  /** INTERNAL pricing-table label — may carry version digits ('Apex-5').
   * Never rendered and never persisted: anything user-adjacent (incl.
   * CostBreakdown.model, which is baked into persisted chat-history cost
   * snapshots) resolves through getPublicSystemNameForUsage() instead,
   * which emits the digit-free public station name ('apex cedar'). */
  displayName: string;
  /** Provider name (codename families: claude=Apex/Nova, perplexity=Echo,
   * openai=Prism, gemini=Titan, grok=Pulse, zai=Zenith, deepseek=Fathom,
   * minimax=Maxim, moonshot=Lunar, xiaomi=Mirage, qwen=Quill, meta=Motif,
   * typesafe=Reflex) */
  provider:
    | 'perplexity'
    | 'claude'
    | 'openai'
    | 'gemini'
    | 'grok'
    | 'zai'
    | 'deepseek'
    | 'minimax'
    | 'moonshot'
    | 'xiaomi'
    | 'qwen'
    | 'meta'
    | 'typesafe'
    | 'unknown';
}

export interface CostBreakdown {
  /** Cost for input tokens */
  inputCost: number;
  /** Cost for output tokens */
  outputCost: number;
  /** Cost for reasoning tokens (if applicable) */
  reasoningCost?: number;
  /** Cost for citation tokens (if applicable) */
  citationCost?: number;
  /** Cost for search queries/tokens (if applicable) */
  searchCost?: number;
  /** Total estimated cost */
  totalCost: number;
  /** PUBLIC digit-free station name for the model ('apex cedar') — this field
   * is persisted into chat-history cost snapshots, so it must never carry
   * version digits or vendor designations (2026-08-27 hygiene). */
  model: string;
  /** Provider */
  provider: string;
}

// ============================================================================
// Model Pricing Data
// ============================================================================

/**
 * Pricing rates for all supported AI models.
 * Rates are in USD per 1 million tokens.
 */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  // Perplexity Models (code name: Echo)
  'sonar-reasoning-pro': {
    inputPerMillion: 2.00,
    outputPerMillion: 8.00,
    reasoningPerMillion: 3.00,
    displayName: 'Echo Reasoning Pro',
    provider: 'perplexity',
  },
  'sonar-deep-research': {
    inputPerMillion: 2.00,
    outputPerMillion: 8.00,
    reasoningPerMillion: 3.00,
    citationPerMillion: 2.00,
    searchPerThousand: 5.00,
    displayName: 'Echo Deep Research',
    provider: 'perplexity',
  },

  // Anthropic Claude Models (code names: Apex, Nova, Swift)
  // Pricing as of February 2026 - extended thinking tokens priced at output rate
  // Always-latest alias — HISTORICAL. Floating aliases are retired (2026-09-29,
  // Justin) and nothing sends one; the row stays so usage recorded under the
  // alias form (the OpenRouter/OpenClaw runtime once sent literal
  // `claude-opus-latest` after provider-prefix stripping) still resolves for the
  // info-icon tooltip instead of falling through to 'unknown'. Frozen at the
  // model the alias served when last checked (Opus 5.5, 2026-09-28) — never
  // re-pointed again.
  'claude-opus-latest': {
    inputPerMillion: 4.00,
    outputPerMillion: 20.00,
    reasoningPerMillion: 20.00,
    displayName: 'Apex-5.5',
    provider: 'claude',
  },
  'claude-opus-5': {
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00,
    displayName: 'Apex-5',
    provider: 'claude',
  },
  // Opus 5.5 (released 2026-09-22; tier-1 lead from the 2026-09-27 re-ladder).
  // $4/$20 — cheaper than Opus 5 — with cache reads $0.20/M (0.05x) and 5-minute
  // cache writes $5/M; thinking is billed at the output rate and cannot be turned
  // off. Verified against the OpenRouter models + endpoints APIs 2026-09-26.
  'claude-opus-5.5': {
    inputPerMillion: 4.00,
    outputPerMillion: 20.00,
    reasoningPerMillion: 20.00,
    displayName: 'Apex-5.5',
    provider: 'claude',
  },
  // Opus latest (current always-latest alias target, created 2026-05-28).
  'claude-opus-4.8': {
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.8',
    provider: 'claude',
  },
  '~anthropic/claude-opus-latest': { // raw always-latest alias id — historical (retired 2026-09-29); frozen at Opus 5.5, what it served when last checked
    inputPerMillion: 4.00,
    outputPerMillion: 20.00,
    reasoningPerMillion: 20.00,
    displayName: 'Apex-5.5',
    provider: 'claude',
  },
  'claude-opus-4-8': { // dash-variant alias of claude-opus-4.8
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00,
    displayName: 'Apex-4.8',
    provider: 'claude',
  },
  'claude-4.8-opus-20260528': { // OpenRouter resolved id for ~anthropic/claude-opus-latest
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00,
    displayName: 'Apex-4.8',
    provider: 'claude',
  },
  'claude-opus-4.7': {
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.7',
    provider: 'claude',
  },
  'claude-opus-4-7': { // dash-variant alias of claude-opus-4.7
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00,
    displayName: 'Apex-4.7',
    provider: 'claude',
  },
  'claude-opus-4-6': {
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.6',
    provider: 'claude',
  },
  'claude-opus-4-5-20251101': { // Legacy backward compat
    inputPerMillion: 5.00,
    outputPerMillion: 25.00,
    reasoningPerMillion: 25.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.5',
    provider: 'claude',
  },
  // Claude Fable 5 (Apogee family, minted 2026-07-10) — Anthropic's frontier
  // model; tier-17 Fallback 1 + tier-18 strategic-innovation PRIMARY per the
  // 2026-07-10 quality-tier reorder. $10/$50 per the official Anthropic card
  // (verified vs OpenRouter 2026-07-10); reasoning is ALWAYS ON and billed at
  // the output rate. 'Fable' is a vendor model designation — never surface it
  // (2026-07-07 naming rule); the codename family is Apogee.
  // Claude Fable 5.1 (Apogee-5.1) — 2026-09-01 rotation (Miles: 'rotate all usage of
  // Fable 5 with Fable 5.1'): the live Fable generation on tiers 1/2/5/16/17/18/19/20.
  // $10/$50 per the official card + live OpenRouter catalog (verified 2026-09-01);
  // reasoning always on at the output rate; cache reads 0.025x on the official card.
  'claude-fable-5.1': {
    inputPerMillion: 10.00,
    outputPerMillion: 50.00,
    reasoningPerMillion: 50.00,
    displayName: 'Apogee-5.1',
    provider: 'claude',
  },
  // Always-latest Fable alias — historical (retired 2026-09-29): frozen at Fable
  // 5.1, what it served when last checked (2026-09-28), so an old usage row
  // carrying the alias form still prices.
  'claude-fable-latest': {
    inputPerMillion: 10.00,
    outputPerMillion: 50.00,
    reasoningPerMillion: 50.00,
    displayName: 'Apogee-5.1',
    provider: 'claude',
  },
  'claude-fable-5': {
    inputPerMillion: 10.00,
    outputPerMillion: 50.00,
    reasoningPerMillion: 50.00,
    displayName: 'Apogee-5',
    provider: 'claude',
  },
  // Sonnet 5 — sanctioned back onto Claude paths 2026-07-02 (Miles,
  // #prest0n-development); tier-2 first fallback per the 2026-07-05 SSOT.
  // Nova family revived for Sonnet majors >= 5 (the 2026-05-10 retirement
  // remap applies to legacy majors <= 4 only).
  // Intro pricing $2/$10 through 2026-08-31, then $3/$15
  // (prest0n/memory/reference/model-tier-stacks.md; verified vs OpenRouter 2026-07-06).
  // The alias row is historical (retired 2026-09-29), frozen at Sonnet 5.5, what
  // it served when last checked (2026-09-28); Sonnet 5.5 costs what Sonnet 5 costs.
  'claude-sonnet-latest': {
    inputPerMillion: 2.00,
    outputPerMillion: 10.00,
    reasoningPerMillion: 10.00,
    displayName: 'Nova-5.5',
    provider: 'claude',
  },
  // Sonnet 5.5 (released 2026-09-28): tier-5 Fallback 2 since 2026-10-02; first priced
  // because the always-latest alias served it. $2/$10 with cached reads $0.20/M on every standard endpoint
  // (OpenRouter endpoints API, 2026-09-28).
  'claude-sonnet-5.5': {
    inputPerMillion: 2.00,
    outputPerMillion: 10.00,
    reasoningPerMillion: 10.00,
    displayName: 'Nova-5.5',
    provider: 'claude',
  },
  'claude-sonnet-5': {
    inputPerMillion: 2.00,
    outputPerMillion: 10.00,
    reasoningPerMillion: 10.00, // Extended thinking tokens billed at output rate
    displayName: 'Nova-5',
    provider: 'claude',
  },
  // Legacy Sonnet/Haiku (majors <= 4) — retired 2026-05-10 per Miles directive.
  // Historical rates are kept for billing fidelity, but the DISPLAY name
  // follows the Apex-4.7 remap so the wallet cost rows match the ⓘ popup
  // codename (getModelCodeName remaps these ids to apex-4.7).
  'claude-sonnet-4-6': {
    inputPerMillion: 3.00,
    outputPerMillion: 15.00,
    reasoningPerMillion: 15.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.7',
    provider: 'claude',
  },
  'claude-sonnet-4-20250514': {
    inputPerMillion: 3.00,
    outputPerMillion: 15.00,
    reasoningPerMillion: 15.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.7',
    provider: 'claude',
  },
  'claude-haiku-4-5-20251001': {
    inputPerMillion: 1.00,
    outputPerMillion: 5.00,
    reasoningPerMillion: 5.00, // Extended thinking tokens billed at output rate
    displayName: 'Apex-4.7',
    provider: 'claude',
  },
  'claude-haiku-4.5': {
    inputPerMillion: 1.00,
    outputPerMillion: 5.00,
    reasoningPerMillion: 5.00,
    displayName: 'Apex-4.7',
    provider: 'claude',
  },

  // ── Perplexity (Echo) — tier-13/14 fallbacks ────────────────────────────
  'sonar-pro': {
    inputPerMillion: 3.00,
    outputPerMillion: 15.00,
    searchPerThousand: 5.00,
    displayName: 'Echo Pro',
    provider: 'perplexity',
  },
  'sonar': {
    inputPerMillion: 1.00,
    outputPerMillion: 1.00,
    searchPerThousand: 5.00,
    displayName: 'Echo',
    provider: 'perplexity',
  },

  // ── OpenAI (Prism) ───────────────────────────────────────────────────────
  // Sol = tier-5 PRIMARY (the unified SWE plane, 2026-07-31 restructure) + fallback
  // rung on tiers 1/2/11/16/17/18. Rates verified vs OpenRouter 2026-07-10.
  'gpt-5.6-sol': {
    inputPerMillion: 5.00,
    outputPerMillion: 30.00,
    reasoningPerMillion: 30.00, // reasoning billed at output rate
    displayName: 'Prism-5.6 S',
    provider: 'openai',
  },
  'gpt-6-astra': { // 2026-09-04 (Miles): T1/T2/T19 PRIMARY + fallback on tiers 4/5/7/11/16/17/18/20/21; official card 2026-09-04 ($10/$50, cached input $1, cache writes $12.50; >272K input bills 2x/1.5x)
    inputPerMillion: 10.00,
    outputPerMillion: 50.00,
    reasoningPerMillion: 50.00,
    displayName: 'Prism-6 A',
    provider: 'openai',
  },
  // 2026-09-30 (tier-4 review): the rest of the GPT-6 generation, priced before any ladder names
  // them (catalog rows read 2026-09-30). Prompts over 272K tokens bill 2x input / 1.5x output.
  'gpt-6.1-sol': { // 2026-09-29 release: $2/$10, cached input $0.10, cache writes $2.50; reasoning is mandatory
    inputPerMillion: 2.00,
    outputPerMillion: 10.00,
    reasoningPerMillion: 10.00,
    displayName: 'Prism-6.1 S',
    provider: 'openai',
  },
  'gpt-6-sol': { // 2026-09-22 release: $2/$10, cached input $0.20, cache writes $2.50
    inputPerMillion: 2.00,
    outputPerMillion: 10.00,
    reasoningPerMillion: 10.00,
    displayName: 'Prism-6 S',
    provider: 'openai',
  },
  'gpt-6-luna': { // 2026-09-22 release: $0.10/$0.50, cached input $0.01, cache writes $0.125
    inputPerMillion: 0.10,
    outputPerMillion: 0.50,
    reasoningPerMillion: 0.50,
    displayName: 'Prism-6 L',
    provider: 'openai',
  },
  'gpt-5.6-luna': { // tier-3 PRIMARY since 2026-07-31 (also tier-15 fallback); rates re-verified vs OpenRouter 2026-07-31 (the old $1/$6 was stale)
    inputPerMillion: 0.10,
    outputPerMillion: 0.60,
    reasoningPerMillion: 0.60,
    displayName: 'Prism-5.6 L',
    provider: 'openai',
  },
  'gpt-5.6-terra': { // ENTERS 2026-07-31: tier-6 PRIMARY + tier-2 Fallback 1 + tier-19 tail; verified vs OpenRouter 2026-07-31
    inputPerMillion: 1.00,
    outputPerMillion: 6.00,
    reasoningPerMillion: 6.00,
    displayName: 'Prism-5.6 T',
    provider: 'openai',
  },
  'gpt-5.3-codex': { // tier-16 xhigh fallback (left tier-2 in the 2026-07-10 grok-4.5 rebuild)
    inputPerMillion: 1.75,
    outputPerMillion: 14.00,
    reasoningPerMillion: 14.00, // codex reasoning is unremovable; billed at output rate
    displayName: 'Prism-5.3',
    provider: 'openai',
  },
  'gpt-5.4': {
    inputPerMillion: 3.00,
    outputPerMillion: 12.00,
    reasoningPerMillion: 12.00,
    // Code name (white-label). Never surface the raw vendor name in user-facing cost UI.
    displayName: 'Prism-5.4',
    provider: 'openai',
  },
  'gpt-5.4-mini': {
    inputPerMillion: 0.75,
    outputPerMillion: 4.50,
    reasoningPerMillion: 4.50,
    displayName: 'Prism-5.4 Mini',
    provider: 'openai',
  },
  'gpt-5.4-nano': {
    inputPerMillion: 0.20,
    outputPerMillion: 1.25,
    reasoningPerMillion: 1.25,
    displayName: 'Prism-5.4 Nano',
    provider: 'openai',
  },
  'gpt-mini-latest': { // historical always-latest mini alias (retired 2026-09-29), priced as gpt-5.4-mini
    inputPerMillion: 0.75,
    outputPerMillion: 4.50,
    reasoningPerMillion: 4.50,
    displayName: 'Prism-5.4 Mini',
    provider: 'openai',
  },
  // Always-latest OpenAI aliases (ids '~openai/gpt-latest' / '~openai/gpt-mini-latest'
  // normalize to these keys) — historical: floating aliases are retired (2026-09-29),
  // so these rows are frozen at gpt-5.6-sol / gpt-5.4-mini for old usage rows.
  'gpt-latest': {
    inputPerMillion: 5.00,
    outputPerMillion: 30.00,
    reasoningPerMillion: 30.00,
    displayName: 'Prism-5.6 S', // retargeted 2026-07-10: the OpenRouter alias now resolves to gpt-5.6-sol (same $5/$30 as 5.5)
    provider: 'openai',
  },
  'gpt-5.5': { // former alias target of ~openai/gpt-latest (historical pricing map — allowlisted)
    inputPerMillion: 5.00,
    outputPerMillion: 30.00,
    reasoningPerMillion: 30.00,
    displayName: 'Prism-5.5',
    provider: 'openai',
  },
  'gpt-4-turbo': { // historical — canonical target of prism-4t/prism-4/prism-3 ledger refs
    inputPerMillion: 10.00,
    outputPerMillion: 30.00,
    displayName: 'Prism-4T',
    provider: 'openai',
  },
  // tier-10-voice fallback rungs (2026-08-19) — OpenRouter endpoints API,
  // verified 2026-08-19. AUDIO rates (see the Titan-3.1 Live note below):
  // gpt-audio audio $32/$64 vs text $2.50/$10; gpt-audio-mini's audio rate
  // equals its text rate at $0.60/$2.40.
  // 2026-09-01 tier-10 re-ladder: OpenAI Realtime rows at the official AUDIO
  // per-M rates (text $4/$16; audio $32/$64 — model pages verified 2026-09-01);
  // audio rates recorded, matching the gpt-audio precedent below.
  'gpt-realtime-1.5': {
    inputPerMillion: 32.0,
    outputPerMillion: 64.0,
    displayName: 'Prism Realtime 1.5',
    provider: 'openai',
  },
  'gpt-realtime-2025-08-28': {
    inputPerMillion: 32.0,
    outputPerMillion: 64.0,
    displayName: 'Prism Realtime',
    provider: 'openai',
  },
  'gpt-audio': {
    inputPerMillion: 32.00,
    outputPerMillion: 64.00,
    displayName: 'Prism Audio',
    provider: 'openai',
  },
  'gpt-audio-mini': {
    inputPerMillion: 0.60,
    outputPerMillion: 2.40,
    displayName: 'Prism Audio Mini',
    provider: 'openai',
  },

  // ── Google Gemini (Titan) ────────────────────────────────────────────────
  // $2/$12 on the standard Google endpoints, thinking at the output rate (OpenRouter
  // endpoints API 2026-09-28; the $10 output and $3 reasoning here were stale — the
  // alias row below was corrected 2026-07-06 and this one never was).
  'gemini-3.1-pro-preview': {
    inputPerMillion: 2.00,
    outputPerMillion: 12.00,
    reasoningPerMillion: 12.00,
    // Code name (white-label). Never surface the raw vendor name in user-facing cost UI.
    displayName: 'Titan-3.1',
    provider: 'gemini',
  },
  // Always-latest Gemini Pro alias ('~google/gemini-pro-latest' normalizes here) —
  // historical (retired 2026-09-29), frozen at gemini-3.1-pro-preview's price. Mirrors
  // the backend WIDGET_MODEL_PRICING 'Titan-Pro' row.
  'gemini-pro-latest': {
    inputPerMillion: 2.00,
    outputPerMillion: 12.00,
    reasoningPerMillion: 12.00,
    displayName: 'Titan-Pro',
    provider: 'gemini',
  },
  // ── tier-10-voice (2026-08-19) ───────────────────────────────────────────
  // Mirrors the backend WIDGET_MODEL_PRICING rows added the same day, so the
  // ⓘ popup's estimate agrees with the wallet debit for voice turns. Before
  // this, no tier-10 model was priced anywhere and voice fell to the $1/$5
  // unknown floor on both sides.
  //
  // These are the AUDIO rates, not the text ones: a voice turn's tokens are
  // overwhelmingly audio tokens, so the text card would under-bill by the same
  // margin the floor did. gemini-3.1-flash-live-preview is Google's official
  // card (audio $3.00/$12.00; text $0.75/$4.50), verified 2026-08-19.
  'gemini-3.1-flash-live-preview': {
    inputPerMillion: 3.00,
    outputPerMillion: 12.00,
    displayName: 'Titan-3.1 Live',
    provider: 'gemini',
  },
  // gemini-3.8-live: the tier-10 primary from 2026-10-16 (the preview above
  // shuts off 2026-11-17). The same official card (audio $3.00/$12.00; text
  // $0.75/$4.50), verified 2026-10-16; the 3.1 row stays for rows it priced.
  'gemini-3.8-live': {
    inputPerMillion: 3.00,
    outputPerMillion: 12.00,
    displayName: 'Titan-3.8 Live',
    provider: 'gemini',
  },
  'gemini-3.5-flash': { // tier-2 fallback rung
    inputPerMillion: 1.50,
    outputPerMillion: 9.00,
    reasoningPerMillion: 9.00,
    displayName: 'Titan-3.5 Flash',
    provider: 'gemini',
  },
  'gemini-3.6-flash': { // HISTORICAL row (left every ladder 2026-08-17) — prices legacy usage refs
    inputPerMillion: 1.50,
    outputPerMillion: 7.50,
    reasoningPerMillion: 7.50,
    displayName: 'Titan-3.6 Flash',
    provider: 'gemini',
  },
  'gemini-3.7-flash': { // HISTORICAL row (left every ladder 2026-09-03) — prices legacy usage refs
    inputPerMillion: 0.75,
    outputPerMillion: 3.75,
    reasoningPerMillion: 3.75,
    displayName: 'Titan-3.7 Flash',
    provider: 'gemini',
  },
  'gemini-3.8-flash': { // latest flash family target (2026-09-03 candidate-flow bump; same rates as 3.7)
    inputPerMillion: 0.75,
    outputPerMillion: 3.75,
    reasoningPerMillion: 3.75,
    displayName: 'Titan-3.8 Flash',
    provider: 'gemini',
  },
  'gemini-flash-latest': { // historical alias row (retired 2026-09-29), frozen at 3.8
    inputPerMillion: 0.75,
    outputPerMillion: 3.75,
    reasoningPerMillion: 3.75,
    displayName: 'Titan-3.8 Flash',
    provider: 'gemini',
  },

  // ── x-ai Grok (Pulse) ────────────────────────────────────────────────────
  // grok-4.6 / grok-latest: tier-4 General Default PRIMARY via the floating
  // ~x-ai/grok-latest alias per the 2026-08-12 re-ladder directive. $2/$6,
  // cached input $0.50/M billed automatically at xAI (rates verified vs the
  // OpenRouter endpoints API 2026-08-12); reasoning billed at the output rate
  // (reasoning_effort low/medium/high/xhigh on 4.6 — the enum WIDENED vs 4.5;
  // default high, cannot be disabled). NOTE the >200k-input long-context
  // surcharge ($4/$12, cache $1/M) is not modeled — flat base rate kept, the
  // same convention as every other row here.
  // grok-4.7 (released 2026-09-21; tier-1 fallback from the 2026-09-27 re-ladder).
  // $2/$6 with cached reads $0.50/M (0.25x) on every standard xAI endpoint
  // (OpenRouter endpoints API, 2026-09-28). The $1.60/$4.80 read on 2026-09-26 was
  // a launch rate that has ended. Prompts over 200k tokens bill double — not
  // modeled, the same convention as grok-4.6. Reasoning at the output rate
  // (reasoning_effort low/medium/high/xhigh, default high; cannot be disabled).
  'grok-4.7': {
    inputPerMillion: 2.00,
    outputPerMillion: 6.00,
    reasoningPerMillion: 6.00,
    displayName: 'Pulse-4.7',
    provider: 'grok',
  },
  'grok-4.6': {
    inputPerMillion: 2.00,
    outputPerMillion: 6.00,
    reasoningPerMillion: 6.00,
    displayName: 'Pulse-4.6',
    provider: 'grok',
  },
  // Floating-alias key — historical (retired 2026-09-29): old usage rows may carry
  // the alias form. Frozen at grok-4.7, what it served when last checked
  // (2026-09-28). Same price as 4.6.
  'grok-latest': {
    inputPerMillion: 2.00,
    outputPerMillion: 6.00,
    reasoningPerMillion: 6.00,
    displayName: 'Pulse-4.7',
    provider: 'grok',
  },
  // grok-4.5: HISTORICAL since the 2026-08-12 all-grok-4.5 follow-up (left
  // every ladder — tier-2 F1 and the tier-14 tail now ride ~x-ai/grok-latest).
  // $2/$6, cached input $0.30/M (0.15x — per-model, NOT the 4.6 ratio; live
  // re-verification 2026-08-16) billed automatically at xAI; reasoning billed
  // at the output rate (reasoning_effort low/medium/high, default high, cannot
  // be disabled). The >200k-input doubling applies here too (not modeled, same
  // convention as every row). Like the 4.20/4.3 rows below (historical since
  // 2026-07-10), keep for billing fidelity — old usage rows still carry these ids.
  'grok-4.5': {
    inputPerMillion: 2.00,
    outputPerMillion: 6.00,
    reasoningPerMillion: 6.00,
    displayName: 'Pulse-4.5',
    provider: 'grok',
  },
  // Meta Muse Spark (code name family: Motif — minted 2026-08-07 with the
  // tier-4 General Default primary promotion; 'muse'/'spark' are vendor model
  // designations and must never be user-visible, 2026-07-07 rule). Rates
  // verified vs the OpenRouter models + endpoints APIs 2026-08-07; reasoning
  // tokens billed at the output rate (effort enum minimal..xhigh; 'none'
  // unsupported). Automatic server-side caching, cached reads $0.15/M.
  'muse-spark-1.3': { // 2026-09-04: tier-19 tail (asked max — xhigh is Meta's public ceiling); OpenRouter catalog 2026-09-04 $1.25/$4.25, cached reads $0.15/M
    inputPerMillion: 1.25,
    outputPerMillion: 4.25,
    reasoningPerMillion: 4.25,
    displayName: 'Motif-1.3',
    provider: 'meta',
  },
  'muse-spark-1.2': {
    inputPerMillion: 1.25,
    outputPerMillion: 4.25,
    reasoningPerMillion: 4.25,
    displayName: 'Motif-1.2',
    provider: 'meta',
  },
  'grok-4.20': {
    inputPerMillion: 1.25,
    outputPerMillion: 2.50,
    displayName: 'Pulse-4.20',
    provider: 'grok',
  },
  'grok-4.3': {
    inputPerMillion: 1.25,
    outputPerMillion: 2.50,
    displayName: 'Pulse-4.3',
    provider: 'grok',
  },

  // ── z-ai GLM (Zenith) — tier-6 tail + tier-20 frontend rung ─────────────
  // OpenRouter blended rates re-verified live 2026-07-31 evening ($1.19/$3.74 —
  // drifted from the morning's $0.97/$3.04; cache reads $0.221/M).
  'glm-5.2': {
    inputPerMillion: 1.19,
    outputPerMillion: 3.74,
    displayName: 'Zenith-5.2',
    provider: 'zai',
  },
  // 2026-08-24 tier-4 re-ladder: GLM 5.3 enters as Fallback 3.
  // Live OpenRouter catalog verified 2026-08-24: $1.40/$4.40 per M, 1.048M ctx.
  'glm-5.3': {
    inputPerMillion: 1.4,
    outputPerMillion: 4.4,
    displayName: 'Zenith-5.3',
    provider: 'zai',
  },
  // 2026-09-01 re-ladder: GLM 5.3 Flash enters (tier-7 Fallback 2).
  // Live OpenRouter catalog verified 2026-09-01: $0.075/$0.25 per M, 1.31M ctx.
  'glm-5.3-flash': {
    inputPerMillion: 0.075,
    outputPerMillion: 0.25,
    displayName: 'Zenith-5.3 Flash',
    provider: 'zai',
  },
  // 2026-09-01 re-ladder: the Qwen3.8 generation enters (tiers 3/4/6/7).
  // Live OpenRouter catalog verified 2026-09-01.
  'qwen3.8-max': {
    inputPerMillion: 2.0,
    outputPerMillion: 6.0,
    displayName: 'Quill-3.8 Max',
    provider: 'qwen',
  },
  // 2026-09-30a: the 2026-09-02 snapshot replaces the 2026-08-03 one on tiers 3/4
  // (qwen/qwen3.8-max left the live catalog). Catalog row read 2026-09-30:
  // $2/$6 per M, 1M ctx. The undated row above stays as a historical billing entry.
  'qwen3.8-max-0902': {
    inputPerMillion: 2.0,
    outputPerMillion: 6.0,
    displayName: 'Quill-3.8 Max 0902',
    provider: 'qwen',
  },
  'qwen3.8-flash': {
    inputPerMillion: 0.15,
    outputPerMillion: 0.47,
    displayName: 'Quill-3.8 Flash',
    provider: 'qwen',
  },
  'qwen3.8-27b': {
    inputPerMillion: 0.425,
    outputPerMillion: 2.55,
    displayName: 'Quill-3.8 27B',
    provider: 'qwen',
  },

  // ── Tier-3 efficiency ladder (account-VM subagent-routine downgrades) ───
  'minimax-m3': {
    inputPerMillion: 0.30,
    outputPerMillion: 1.20,
    displayName: 'Maxim-3',
    provider: 'minimax',
  },
  'deepseek-v4-pro': {
    inputPerMillion: 0.44,
    outputPerMillion: 0.87,
    displayName: 'Fathom-4 Pro',
    provider: 'deepseek',
  },
  // 2026-08-24 tier-4 re-ladder: the DATED 0813 build enters as the tail —
  // a distinct serving model with distinct pricing from the undated row above.
  // Live OpenRouter catalog verified 2026-08-24: $1.122/$3.366 per M, 1.048M ctx.
  'deepseek-v4-pro-0813': {
    inputPerMillion: 1.122,
    outputPerMillion: 3.366,
    displayName: 'Fathom-4 Pro 0813',
    provider: 'deepseek',
  },
  'kimi-k2.6': {
    inputPerMillion: 0.66,
    outputPerMillion: 3.41,
    displayName: 'Lunar-2.6',
    provider: 'moonshot',
  },
  'kimi-k2.7-code': { // tier-6 Fallback 1 (2026-07-11 latest-sweep bump; tier-3 keeps general k2.6)
    inputPerMillion: 0.72,
    outputPerMillion: 3.50,
    displayName: 'Lunar-2.7 Code',
    provider: 'moonshot',
  },
  // Kimi K3 — Moonshot flagship; tier-5 rung 4 since the 2026-07-31 restructure
  // (entered as a tier-2 rung 2026-07-17). $3/$15, 1M ctx, implicit cache reads $0.30/M
  // (verified vs OpenRouter models API + /endpoints 2026-07-17); reasoning
  // billed at the output rate.
  'kimi-k3': {
    inputPerMillion: 3.00,
    outputPerMillion: 15.00,
    reasoningPerMillion: 15.00,
    displayName: 'Lunar-3',
    provider: 'moonshot',
  },
  'mimo-v2.5-pro': {
    inputPerMillion: 0.44,
    outputPerMillion: 0.87,
    displayName: 'Mirage-2.5 Pro',
    provider: 'xiaomi',
  },

  // ── Qwen (Quill) — tier-11 fallback rung ────────────────────────────────
  'qwen3.7-max': {
    inputPerMillion: 1.48,
    outputPerMillion: 4.42,
    displayName: 'Quill-3.7 Max',
    provider: 'qwen',
  },

  // ── TypeSafe Jev (Reflex) — Tier 23 Fast Decisions primary (2026-09-28) ─
  // Decisions API: typed answers, no text. $0.042 per M input tokens, output
  // free (docs.typesafe.ai + the OpenRouter guide, read 2026-09-27). Keyed with
  // its vendor segment: 'typesafe/' is not a stripped transport prefix, so
  // normalizeModelKey leaves it on and the lookup lands here directly.
  'typesafe/jev-1.13': {
    inputPerMillion: 0.042,
    outputPerMillion: 0,
    displayName: 'Reflex-1.13',
    provider: 'typesafe',
  },

  // Fallback for unknown models.
  // NOTE: these floor rates intentionally MATCH the backend unknown-model
  // floor in functions/src/index.ts calculateWidgetTokenCost — the wallet
  // bills unknown models at this floor, so the ⓘ popup must estimate with
  // the same rates instead of showing $0.00 for a turn that produced a real
  // debit. hasKnownPricing() still reports false for unknown models.
  'unknown': {
    inputPerMillion: 1.00,
    outputPerMillion: 5.00,
    displayName: 'unknown model',
    provider: 'unknown',
  },
};

// ============================================================================
// Cost Calculation
// ============================================================================

// User-facing cost multiplier — imported from agenticWorkflowConfig.ts (canonical source)

/**
 * Calculate the cost breakdown for a given token usage.
 * Costs are adjusted for user display.
 *
 * @param tokenUsage - Token usage metadata from an AI response
 * @returns Cost breakdown with per-category and total costs
 */
// Reverse-lookup: codename → canonical model key
// Keeps pricing table DRY — codenames alias to their canonical entry.
// Exported for the tier-coverage CI gate, which asserts value-lockstep with
// the backend copy (WIDGET_CODENAME_TO_CANONICAL in
// functions/src/lib/accountExpense.ts). Two more went with the retired
// whole-turn lane (D15): proxyClient.ts's, and the web adapter's CODENAME_MAP
// with its dead turn handlers (2026-09-28).
export const CODENAME_TO_CANONICAL: Record<string, string> = {
  // Apex (Claude Opus). `apex` (unversioned) names the current Opus; bump it with
  // each new Opus. `apex-latest` is historical (the alias it named is retired,
  // 2026-09-29), frozen at Opus 5.5.
  'apex-5.5': 'claude-opus-5.5', // 2026-09-27 tier-1 lead ('apex' follows)
  'apex-5': 'claude-opus-5',
  'apex-4.8': 'claude-opus-4.8',
  'apex-latest': 'claude-opus-5.5',
  'apex-4.7': 'claude-opus-4.7',
  'apex-4.6': 'claude-opus-4-6',
  'apex-4.5': 'claude-opus-4-5-20251101',
  'apex': 'claude-opus-5.5',
  // Apogee (Claude Fable — frontier family, minted 2026-07-10).
  'apogee-5.1': 'claude-fable-5.1', // 2026-09-01 rotation: the live Fable generation
  'apogee-5': 'claude-fable-5',
  'apogee': 'claude-fable-5.1', // unversioned tracks the live generation (2026-09-01)
  // Nova (Claude Sonnet). Revived for majors >= 5 (2026-07-02 Sonnet-5
  // sanction). Legacy majors <= 4 stay remapped to Opus-4-7 per the
  // 2026-05-10 retirement directive.
  'nova-5.5': 'claude-sonnet-5.5', // 2026-09-28: the always-latest alias serves it
  'nova-5': 'claude-sonnet-5',
  'nova-4.6': 'claude-opus-4-7',
  'nova-4': 'claude-opus-4-7',
  'nova': 'claude-opus-4-7',
  // Swift — retired 2026-05-10 per Miles directive (Haiku removed from all
  // Claude paths). Legacy refs remap to Opus-4-7.
  'swift-4.5': 'claude-opus-4-7',
  'swift': 'claude-opus-4-7',
  // Echo (Perplexity Sonar)
  'echo-reasoning-pro': 'sonar-reasoning-pro',
  'echo-deep': 'sonar-deep-research',
  'echo-pro': 'sonar-pro', // fixed 2026-07-06: sonar-pro is its own model/price, not reasoning-pro
  'echo-reasoning': 'sonar-reasoning-pro',
  // 'echo' (bare) was only ever produced by the \bsonar\b regex fallthrough,
  // i.e. for the base 'sonar' model — it aliased to reasoning-pro merely
  // because no 'sonar' row existed. Now that bare sonar is a routed tier-13
  // rung with its own row, point the alias at the accurate target.
  'echo': 'sonar',
  'echo-search': 'sonar', // 2026-08-21: base search's own code (mint word 'moor'); legacy 'echo' rows still resolve
  // Prism (OpenAI GPT)
  'prism-5.6-s': 'gpt-5.6-sol',
  'prism-5.6-t': 'gpt-5.6-terra',
  'prism-6-a': 'gpt-6-astra', // 2026-09-04
  'prism-6.1-s': 'gpt-6.1-sol', // 2026-09-30
  'prism-6-s': 'gpt-6-sol', // 2026-09-30
  'prism-6-l': 'gpt-6-luna', // 2026-09-30
  'prism-5.6-l': 'gpt-5.6-luna',
  'prism-5.6-sol': 'gpt-5.6-sol', // LEGACY alias (pre-2026-07-31 records — variant-name codenames retired: real variant names like 'terra' were user-visible on the platform)
  'prism-5.6-terra': 'gpt-5.6-terra', // LEGACY alias (pre-2026-07-31 records — variant-name codenames retired: real variant names like 'terra' were user-visible on the platform)
  'prism-5.6-luna': 'gpt-5.6-luna', // LEGACY alias (pre-2026-07-31 records — variant-name codenames retired: real variant names like 'terra' were user-visible on the platform)
  'prism-5.5': 'gpt-5.5',
  'prism-latest': 'gpt-5.6-sol', // retargeted 2026-07-10 (alias now resolves to 5.6 Sol)
  'prism-5.4': 'gpt-5.4',
  'prism-5.4-mini': 'gpt-5.4-mini',
  'prism-5.4-nano': 'gpt-5.4-nano',
  'prism-5.3': 'gpt-5.3-codex',
  // 2026-07-07 (Miles directive): vendor model designations must not leak
  // into codenames — 'codex'/'K2'/'M3' dropped. Old spellings stay as
  // resolve-only aliases for records persisted before the rename.
  'prism-5.3-codex': 'gpt-5.3-codex', // legacy alias (pre-rename records)
  'prism-codex': 'gpt-5.3-codex', // legacy alias (pre-rename records)
  'prism-mini': 'gpt-5.4-mini',
  'prism-4t': 'gpt-4-turbo',
  'prism-4': 'gpt-4-turbo',
  'prism-3': 'gpt-4-turbo',
  'prism-realtime-1.5': 'gpt-realtime-1.5', // 2026-09-01 tier-10 re-ladder
  'prism-realtime': 'gpt-realtime-2025-08-28',
  'prism-audio': 'gpt-audio',
  'prism-audio-mini': 'gpt-audio-mini',
  // Titan (Google Gemini)
  'titan-pro': 'gemini-pro-latest',
  'titan-3.8-flash': 'gemini-3.8-flash',
  'titan-3.7-flash': 'gemini-3.7-flash',
  'titan-3.6-flash': 'gemini-3.6-flash',
  'titan-3.5-flash': 'gemini-3.5-flash',
  'titan-3.1': 'gemini-3.1-pro-preview',
  'titan-3.1-live': 'gemini-3.1-flash-live-preview',
  'titan-3.8-live': 'gemini-3.8-live',
  'titan': 'gemini-3.1-pro-preview',
  // Pulse (x-ai Grok). Unversioned names the current Grok — grok-4.7 since
  // 2026-09-28 (grok-4.6 from the 2026-08-12 re-ladder; grok-4.5 per the
  // 2026-07-10 REBUILD).
  'pulse-4.7': 'grok-4.7', // 2026-09-27 tier-1 fallback
  'pulse-4.6': 'grok-4.6',
  'pulse-4.5': 'grok-4.5',
  'pulse-4.20': 'grok-4.20',
  'pulse-4.3': 'grok-4.3',
  'pulse': 'grok-4.7',
  // Zenith (z-ai GLM)
  'zenith-5.3-flash': 'glm-5.3-flash', // 2026-09-01 re-ladder
  'zenith-5.3': 'glm-5.3', // 2026-08-24 tier-4 re-ladder
  'zenith-5.2': 'glm-5.2',
  'zenith': 'glm-5.2',
  // Maxim (MiniMax)
  'maxim-3': 'minimax-m3',
  'maxim-m3': 'minimax-m3', // legacy alias (pre-rename records)
  'maxim': 'minimax-m3',
  // Fathom (DeepSeek)
  'fathom-4-pro-0813': 'deepseek-v4-pro-0813', // 2026-08-24 tier-4 re-ladder (dated build)
  'fathom-4-pro': 'deepseek-v4-pro',
  'fathom': 'deepseek-v4-pro',
  // Lunar (Moonshot Kimi)
  'lunar-3': 'kimi-k3', // tier-2 Fallback 1 (2026-07-17 K3-insertion directive)
  'lunar-2.7-code': 'kimi-k2.7-code', // tier-6 rung; '-code' is a variant descriptor (like Sol/Luna), kept to disambiguate any future general k2.7
  'lunar-2.6': 'kimi-k2.6',
  'lunar-k2.6': 'kimi-k2.6', // legacy alias (pre-rename records)
  'lunar': 'kimi-k2.6', // unversioned tracks the general line (tier-3 rung)
  // Mirage (Xiaomi MiMo)
  'mirage-2.5-pro': 'mimo-v2.5-pro',
  'mirage': 'mimo-v2.5-pro',
  // Quill (Qwen)
  'quill-3.8-max': 'qwen3.8-max', // 2026-09-01 re-ladder
  'quill-3.8-max-0902': 'qwen3.8-max-0902', // 2026-09-30a (dated snapshot, tiers 3/4)
  'quill-3.8-flash': 'qwen3.8-flash',
  'quill-3.8-27b': 'qwen3.8-27b',
  'quill-3.7-max': 'qwen3.7-max',
  'quill': 'qwen3.7-max',
  // Motif (Meta Muse Spark — minted 2026-08-07, tier-4 General Default primary)
  'motif-1.3': 'muse-spark-1.3', // 2026-09-04
  'motif-1.2': 'muse-spark-1.2',
  'motif': 'muse-spark-1.2',
  // Reflex (TypeSafe System One decisions — Tier 23, 2026-09-28)
  'reflex-1.13': 'typesafe/jev-1.13',
};

/**
 * Normalize a model identifier to its canonical pricing table key.
 * Handles codenames (apex-4.6), provider-prefixed IDs (openrouter/anthropic/claude-opus-4-6),
 * and direct canonical IDs (claude-opus-4-6).
 * Exported for the tier-coverage CI test (tests/tools/modelTierPricingCoverage.test.ts),
 * which asserts every routed-tier ladder id resolves to a real pricing row.
 */
export function normalizeModelKey(model: string | undefined): string {
  if (!model) return 'unknown';
  const raw = model.trim();
  // Strip provider prefix(es) one or more times, including the optional `~`
  // always-latest sigil OpenRouter / OpenClaw uses (e.g. `~anthropic/...`).
  // Examples normalized:
  //   openrouter/~anthropic/claude-opus-latest -> claude-opus-latest
  //   ~anthropic/claude-opus-latest            -> claude-opus-latest
  //   anthropic/claude-opus-latest             -> claude-opus-latest
  let stripped = raw;
  for (let i = 0; i < 4; i += 1) {
    const next = stripped.replace(/^~?(openrouter|anthropic|openai|google|perplexity|x-ai|z-ai|groq|deepseek|qwen|moonshotai|minimax|xiaomi|meta-llama|meta)\//i, '');
    if (next === stripped) break;
    stripped = next;
  }
  // Check codename reverse-lookup first
  if (CODENAME_TO_CANONICAL[stripped.toLowerCase()]) {
    return CODENAME_TO_CANONICAL[stripped.toLowerCase()];
  }
  // Check if it's directly in the pricing table
  if (MODEL_PRICING[stripped]) return stripped;
  // Try lowercase version
  if (MODEL_PRICING[stripped.toLowerCase()]) return stripped.toLowerCase();
  return stripped;
}

export function calculateCost(tokenUsage: TokenUsageMetadata | undefined): CostBreakdown | null {
  if (!tokenUsage) return null;

  const modelKey = normalizeModelKey(tokenUsage.model);
  const pricing = MODEL_PRICING[modelKey] || MODEL_PRICING['unknown'];

  const inputTokens = tokenUsage.inputTokens || 0;
  const outputTokens = tokenUsage.outputTokens || 0;
  const reasoningTokens = tokenUsage.reasoningTokens || 0;
  const citationTokens = tokenUsage.citationTokens || 0;
  const searchQueries = tokenUsage.searchQueries || 0;

  // If OpenRouter reported the authoritative upstream cost (includes search fees,
  // reasoning, and other provider-specific charges), use it as the base cost.
  // This is more accurate than reconstructing from token counts, especially for
  // Perplexity models where search query costs aren't returned as separate token counts.
  if (tokenUsage.upstreamCostUsd && tokenUsage.upstreamCostUsd > 0) {
    const baseCost = tokenUsage.upstreamCostUsd;
    const totalCost = baseCost * USER_COST_MULTIPLIER;

    // Still break down what we can for transparency, but ensure total matches upstream
    const baseInputCost = (inputTokens / 1_000_000) * pricing.inputPerMillion;
    const baseOutputCost = (outputTokens / 1_000_000) * pricing.outputPerMillion;
    const baseReasoningCost = pricing.reasoningPerMillion
      ? (reasoningTokens / 1_000_000) * pricing.reasoningPerMillion
      : undefined;

    // The remainder (upstream cost minus what we can attribute to tokens) is search/other fees
    const attributedBase = baseInputCost + baseOutputCost + (baseReasoningCost || 0);
    const baseSearchAndOther = Math.max(0, baseCost - attributedBase);

    return {
      inputCost: baseInputCost * USER_COST_MULTIPLIER,
      outputCost: baseOutputCost * USER_COST_MULTIPLIER,
      reasoningCost: baseReasoningCost !== undefined ? baseReasoningCost * USER_COST_MULTIPLIER : undefined,
      citationCost: undefined,
      searchCost: baseSearchAndOther > 0.000001 ? baseSearchAndOther * USER_COST_MULTIPLIER : undefined,
      totalCost,
      // Digit-free public station name (2026-08-27 hygiene): this value is
      // baked into persisted cost snapshots, so it rides the same resolver
      // every visible surface uses — never the internal displayName.
      model: getPublicSystemNameForUsage(tokenUsage.model, pricing.provider),
      provider: pricing.provider,
    };
  }

  // Fallback: Calculate from token counts and our pricing table
  // Calculate base costs (tokens / 1,000,000 * rate, except search which is per 1K queries)
  const baseInputCost = (inputTokens / 1_000_000) * pricing.inputPerMillion;
  const baseOutputCost = (outputTokens / 1_000_000) * pricing.outputPerMillion;
  const baseReasoningCost = pricing.reasoningPerMillion
    ? (reasoningTokens / 1_000_000) * pricing.reasoningPerMillion
    : undefined;
  const baseCitationCost = pricing.citationPerMillion
    ? (citationTokens / 1_000_000) * pricing.citationPerMillion
    : undefined;
  // Search is billed per 1K queries, not per 1M tokens
  const baseSearchCost = pricing.searchPerThousand
    ? (searchQueries / 1_000) * pricing.searchPerThousand
    : undefined;

  // Apply user-facing multiplier to all costs
  const inputCost = baseInputCost * USER_COST_MULTIPLIER;
  const outputCost = baseOutputCost * USER_COST_MULTIPLIER;
  const reasoningCost = baseReasoningCost !== undefined ? baseReasoningCost * USER_COST_MULTIPLIER : undefined;
  const citationCost = baseCitationCost !== undefined ? baseCitationCost * USER_COST_MULTIPLIER : undefined;
  const searchCost = baseSearchCost !== undefined ? baseSearchCost * USER_COST_MULTIPLIER : undefined;

  const totalCost = inputCost + outputCost + (reasoningCost || 0) + (citationCost || 0) + (searchCost || 0);

  return {
    inputCost,
    outputCost,
    reasoningCost,
    citationCost,
    searchCost,
    totalCost,
    // Same digit-free contract as the upstream-cost branch above.
    model: getPublicSystemNameForUsage(tokenUsage.model, pricing.provider),
    provider: pricing.provider,
  };
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a cost value as a USD string.
 * Shows up to 8 decimal places for very small amounts for better transparency.
 * Uses the generalized formatUsd utility.
 *
 * @param cost - Cost in USD
 * @returns Formatted cost string (e.g., "$0.0023")
 */
export function formatCost(cost: number | undefined): string {
  if (cost === undefined || cost === null) return '$0.00';
  return formatUsd(cost);
}

/**
 * Get the pricing info for a model.
 * 
 * @param model - Model identifier
 * @returns Model pricing or undefined if not found
 */
export function getModelPricing(model: string | undefined): ModelPricing | undefined {
  if (!model) return undefined;
  return MODEL_PRICING[normalizeModelKey(model)];
}

/**
 * Check if a model has known pricing.
 * Handles codenames (apex-4.6), provider-prefixed IDs, and canonical IDs.
 *
 * @param model - Model identifier
 * @returns true if pricing is available
 */
export function hasKnownPricing(model: string | undefined): boolean {
  if (!model) return false;
  const key = normalizeModelKey(model);
  return key in MODEL_PRICING && key !== 'unknown';
}
